import { BlankSlate } from "@msuite/ui/blank-slate";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Stats } from "@msuite/ui/stats";
import { calculateTimeBySeconds, getCalculatedTimeEasyString, moment } from "@msuite/utils/date";
import type { Tables } from "@msuite/utils/types";
import {
	getEmploymentDailyTargetSeconds,
	getEmploymentForDate,
	getIsScheduledDay,
} from "@msuite/utils/users";
import { useMemo } from "react";
import { useAttendancesHistory } from "@/hooks/use-attendances-history";
import { useUserEmployments } from "@/hooks/use-user-employments";

/** Props Interface */
interface AttendanceInsightsProps {
	userId: string;
}

/** Seconds as the compact duration every figure on this card is written in — "8 h 12 min". */
function toDuration(seconds: number) {
	return getCalculatedTimeEasyString(calculateTimeBySeconds(seconds));
}

/** A balance reads as a direction, so it carries its sign and is written as a magnitude. */
function toBalance(seconds: number) {
	return `${seconds < 0 ? "−" : "+"}${toDuration(Math.abs(seconds))}`;
}

export function AttendanceInsights({ userId }: AttendanceInsightsProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { history, isLoading } = useAttendancesHistory(userId);
	const employmentsQuery = useUserEmployments(userId);

	/** Computed */
	const employments = useMemo(
		() => (employmentsQuery.data?.employments ?? []) as Array<Tables<"employments">>,
		[employmentsQuery.data],
	);

	// The history is a zero-filled series of the last 30 days, one row per day, so a scheduled day
	// nobody worked is present and reads as zero.
	//
	// The averages divide by the scheduled days that carry work rather than by every scheduled day,
	// and that is the figure's meaning: a fortnight of leave would otherwise halve it and read as a
	// run of short days. `days_worked` names the divisor beside it, so the basis is on the card
	// rather than in somebody's head.
	const insights = useMemo(() => {
		const today = moment().db();
		let scheduledWork = 0;
		let scheduledBreak = 0;
		let offScheduleWork = 0;
		let scheduledDays = 0;
		let workedDays = 0;
		let pendingEntries = 0;
		let targetSeconds = 0;
		let closedWork = 0;
		let hasTarget = false;

		for (const day of history) {
			pendingEntries += day.pendingEntries;
			const dateMoment = moment(day.date);
			const employment = getEmploymentForDate(employments, dateMoment.db());
			if (!getIsScheduledDay(employment, dateMoment)) {
				offScheduleWork += day.totalWork;
				continue;
			}
			scheduledDays += 1;
			scheduledWork += day.totalWork;
			scheduledBreak += day.totalBreak;
			if (day.totalWork > 0) workedDays += 1;

			// The balance weighs closed days only. Today is half-lived, and charging it a whole
			// day's target would show a deficit every morning that the afternoon repays.
			if (employment && dateMoment.db() < today) {
				hasTarget = true;
				targetSeconds += getEmploymentDailyTargetSeconds(employment);
				closedWork += day.totalWork;
			}
		}

		return {
			averageWork: workedDays ? Math.round(scheduledWork / workedDays) : 0,
			averageBreak: workedDays ? Math.round(scheduledBreak / workedDays) : 0,
			totalWork: scheduledWork + offScheduleWork,
			offScheduleWork,
			scheduledDays,
			workedDays,
			pendingEntries,
			// Without a contract there is nothing to owe against, and a balance against a target of
			// zero would read every hour worked as overtime.
			balance: hasTarget ? closedWork - targetSeconds : null,
		};
	}, [history, employments]);

	/** Render */
	return (
		<ContentCard
			icon={icon("Gauge")}
			title={t("users.attendance_insights")}
		>
			{isLoading && <BlankSlate isLoading />}
			{!isLoading && (
				<Stats.Container>
					<Stats>
						<Stats.Label>{t("users.average_per_workday")}</Stats.Label>
						<Stats.Value forceNumber={false}>{toDuration(insights.averageWork)}</Stats.Value>
					</Stats>
					<Stats>
						<Stats.Label>{t("users.days_worked")}</Stats.Label>
						<Stats.Value forceNumber={false}>
							{`${insights.workedDays} / ${insights.scheduledDays}`}
						</Stats.Value>
					</Stats>
					{insights.balance !== null && (
						<Stats>
							<Stats.Label>{t("users.attendance_balance")}</Stats.Label>
							<Stats.Value forceNumber={false}>{toBalance(insights.balance)}</Stats.Value>
						</Stats>
					)}
					<Stats>
						<Stats.Label>{t("users.total_work")}</Stats.Label>
						<Stats.Value forceNumber={false}>{toDuration(insights.totalWork)}</Stats.Value>
					</Stats>
					<Stats>
						<Stats.Label>{t("users.average_break")}</Stats.Label>
						<Stats.Value forceNumber={false}>{toDuration(insights.averageBreak)}</Stats.Value>
					</Stats>
					<Stats>
						<Stats.Label>{t("users.off_schedule_work")}</Stats.Label>
						<Stats.Value forceNumber={false}>{toDuration(insights.offScheduleWork)}</Stats.Value>
					</Stats>
					<Stats>
						<Stats.Label>{t("users.pending_entries")}</Stats.Label>
						<Stats.Value>{insights.pendingEntries}</Stats.Value>
					</Stats>
				</Stats.Container>
			)}
		</ContentCard>
	);
}
